import { DragDropEvent } from "services/dragdrop/DragDropContext";
import { AvailabilityByDayType, EventsByResourceType, EventsListType, SchedulerDraggableId, SchedulerDroppableId } from "../../SchedulingTypes";
import { saveData } from "services/api/saveData";
import { calculateDurationMinutesFromDateTimes, determineNearestTimeSlot } from "../../functions/schedulingDateUtils";
import { t } from "i18next";

export const handleUnassignEmployeeDrag = async (
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId>,
    setFloatingAlert: Function,
    setEvents: React.Dispatch<React.SetStateAction<EventsListType>>,
    setEventsByResource: React.Dispatch<React.SetStateAction<EventsByResourceType>>,
    setAvailabilityByDay: React.Dispatch<React.SetStateAction<AvailabilityByDayType>>
) => {

    // Get the values from the drag result
    const { destination, draggableId } = dragResult;

    if (!destination) return;

    // Get the source type, destination type and draggable type
    const destinationType = destination.droppableId.type;

    // Handle the drag of a job employee event to the available employees
    if ((draggableId.type === 'projectJobEmployeeEvent' || draggableId.type === 'individualJobEmployeeEvent') && destinationType == 'availableEmployeesRow') {

        // Get the values of the draggable event
        const { appointmentId, assigneeId, assigneeName, key: eventKey, resourceId } = draggableId.event;

        // Get the destination values
        const { day: destinationDay } = destination.droppableId;

        // Only allow dragging from and to the same day
        if (draggableId.event.dayKey !== destinationDay) {
            setFloatingAlert({
                type: "danger",
                message: t('scheduling.scheduling_board.cannot_drag_to_another_day')
            });
            return;
        }

        try {
            // Unassign the employee from the appointment in the backend
            const response = await saveData({
                apiUrl: `project_scheduler_unassign_employee`,
                method: 'post',
                data: {
                    appointment_id: appointmentId,
                    user_id: assigneeId,
                    day: destinationDay
                }
            })

            // Update the event state
            if (response?.status === 200) {

                // Remove the original event from the project job or individual job resource
                setEventsByResource(prev => {
                    const updated = { ...prev }

                    const resourceKey = String(resourceId)
                    const resourceEvents = updated[resourceKey] || []

                    updated[resourceKey] = resourceEvents.filter(
                        event_key => event_key !== eventKey
                    )

                    return updated
                })

                // Delete the original event
                setEvents(prev => {
                    const updated = { ...prev }
                    if (updated[eventKey]) {
                        delete updated[eventKey]
                    }
                    return updated
                })

                // Generate/update the availability events of the user
                setAvailabilityByDay(prev => {
                    // Copy the current state
                    const newAvailabilityByDay = { ...prev }

                    // Get the data from the new availability
                    const { user_id, date, blocks } = response.data.new_availability;

                    // Convert the date to a sting
                    const dateKey = String(date);

                    // Add the date to the new availability by day state if it does not exists
                    if (!newAvailabilityByDay[dateKey]) newAvailabilityByDay[dateKey] = {};

                    // Create new events for the user
                    const newEvents = blocks.map((block: { start: string, end: string }) => {

                        // Generate local start and end datetimes
                        const startDateTime = `${dateKey}T${block.start}:00`;
                        const endDateTime = `${dateKey}T${block.end}:00`;

                        // Generate a new event key
                        const key = `availableEmployee-${user_id}-${dateKey}-${block.start}`;

                        return {
                            key,
                            type: "availableEmployee",
                            resourceId: "available_employees",
                            startDateTime,
                            endDateTime,
                            dayKey: dateKey,
                            startTime: block.start,
                            endTime: block.end,
                            durationMinutes: calculateDurationMinutesFromDateTimes(startDateTime, endDateTime),
                            timeSlot: determineNearestTimeSlot(startDateTime),
                            employeeId: user_id,
                            employeeName: assigneeName,
                            deleted: false
                        }
                    });

                    // Replace availability for this user/day
                    newAvailabilityByDay[dateKey][user_id] = newEvents;

                    return newAvailabilityByDay;
                })

                // Show floating alert on success
                setFloatingAlert({
                    type: "success",
                    message: t('scheduling.scheduling_board.employee_removed_from_job', { employee: assigneeName })
                });
            }
        } catch (error) {
            setFloatingAlert({
                type: "danger",
                message: t('scheduling.scheduling_board.employee_unschedule_error')
            });
        }
    }
}